import getServerSideData from "@/services/graph-ql/ssr";
import {
  createDataClient,
  createPublicDataClient,
} from "@/services/data-client/DataClientFactory";
import { createSlice } from "@reduxjs/toolkit";
import { defaultTagColors } from "@/services/tag-colors";
import FeatureService from "@/services/feature-service";
import dropdownEnums, { Enums, FeatureKeys } from "@/services/dropdown-enums";
import { sortByDisplayName } from "@/services/generic.methods";

export const dropdownOptions = createSlice({
  name: "dropdownOptions",
  initialState: {
    dropdownOptions: {},
  },

  reducers: {
    setRepresentativesNameToState: (state, action) => {
      state.dropdownOptions.representativesName = action.payload;
    },
    setLeadStatusesToState: (state, action) => {
      state.dropdownOptions.leadStatuses = action.payload;
    },
    setTaskStatusToState: (state, action) => {
      state.dropdownOptions.taskStatuses = action.payload;
    },
    setTaskTypeToState: (state, action) => {
      state.dropdownOptions.taskTypes = action.payload;
    },
    setTaskPriorityToState: (state, action) => {
      state.dropdownOptions.taskPriorities = action.payload;
    },
    setLanguagesToState: (state, action) => {
      state.dropdownOptions.languages = action.payload;
    },
    setLeadSourcesToState: (state, action) => {
      state.dropdownOptions.leadSources = action.payload;
    },
    setUsersToState: (state, action) => {
      state.dropdownOptions.users = sortByDisplayName(action.payload);
    },
    setMembersToState: (state, action) => {
      state.dropdownOptions.members = action.payload;
    },
    setCrmMembersToState: (state, action) => {
      state.dropdownOptions.crmMembers = action.payload;
    },
    setCrmCalendarsToState: (state, action) => {
      state.dropdownOptions.crmCalendars = action.payload;
    },
    setLeadContactedUsersToState: (state, action) => {
      state.dropdownOptions.leadContactedUsers = action.payload;
    },
    setLeadOptionsToState: (state, action) => {
      state.dropdownOptions = { ...state.dropdownOptions, ...action.payload };
    },
    setDomainEmailsToState: (state, action) => {
      let options = action.payload;
      options = options.map((opt) => {
        opt.label = `${opt.alias} (${opt.label})`;
        return opt;
      });
      state.dropdownOptions.domainEmails = options;
    },
    setTagsToState: (state, action) => {
      state.dropdownOptions.tags = action.payload;
    },
    addNewTagToOptions: (state, action) => {
      state.dropdownOptions.tags = [
        ...state.dropdownOptions.tags,
        action.payload,
      ];
    },
    deleteTagFromOptions: (state, action) => {
      const { id } = action.payload;
      state.dropdownOptions.tags = state.dropdownOptions.tags.filter(
        (tag) => tag.id !== id
      );
    },

    setRequestTypesToState: (state, action) => {
      state.dropdownOptions.requestTypes = action.payload;
    },
  },

  // extraReducers: (builder) => {
  //   // hydrate previous state while ssr
  //   builder
  //     .addCase(HYDRATE, (state, action) => {
  //       // Merge the hydrated state with the existing state
  //       if(typeof window !== 'undefined') {
  //         return {
  //           ...state,
  //           ...action.payload.dropdownOptions, // set server/client side states to avoid refetching
  //         };
  //       }
  //       return {
  //         ...state,
  //         dropdownOptions: {}
  //       }
  //     })
  //     // update state when createAsyncThunk return async data
  //     .addCase(getOptionsForCreateLead.fulfilled, (state, action) => {
  //       state.dropdownOptions = action.payload;
  //     })
  // },
});

export const {
  setLeadStatusesToState,
  setLanguagesToState,
  setLeadSourcesToState,
  setTagsToState,
  setUsersToState,
  setMembersToState,
  setLeadOptionsToState,
  setDomainEmailsToState,
  addNewTagToOptions,
  deleteTagFromOptions,
  setLeadContactedUsersToState,
  setTaskStatusToState,
  setTaskTypeToState,
  setTaskPriorityToState,
  setRepresentativesNameToState,
  setCrmMembersToState,
  setRequestTypesToState,
  setCrmCalendarsToState,
} = dropdownOptions.actions;

/**
 * These function called thunk and allows to perform async logic
 * It can be dispatched like a regular action: `dispatch(data)`
 **/
/** @name: @getOptionsForCreateLead
 *  @params: payload => query payload
 *  @description: Create an async thunk for fetching data
 */
export const getOptionsForCreateLead =
  (payload, isPublic, dataClient) => async (dispatch, getState) => {
    const resolvedDataClient =
      dataClient ||
      (isPublic ? createPublicDataClient("basic") : createDataClient("basic"));
    const excludedRoles = ["SalesManagerReadonly"];
    const crmRoles = [
      "CRMAdmin",
      "CRMDefaultManager",
      "SalesManager",
      "SalesManagerReadonly",
      "SchedulingUser",
      "SchedulingTeamManager",
    ];
    // let {
    //   dropdownOptions: { dropdownOptions }
    // } = getState();

    // // check if options data is already fetched or cached
    // if(Object.keys(dropdownOptions)?.length) {
    //   return dropdownOptions
    // }
    // get data from gql
    const state = getState();
    const user = state.auth?.profile;
    const features = FeatureService.resolveFeatures(user);
    const isBasic = !FeatureService.hasFeature(features, FeatureKeys.CRM);

    let options = {};
    const data = await getServerSideData(payload, isPublic);
    options = JSON.parse(JSON.stringify(data || {}));

    if (!isBasic) {
      if (options?.leadStatuses?.length) {
        options.leadStatuses = options?.leadStatuses?.sort(
          (a, b) => a.sortOrder - b.sortOrder
        );
      }
    } else {
      options.tagColors = defaultTagColors;
      try {
        const { LeadModel } = await import("@blazeo.com/calendar-client");
        const sources = await LeadModel.getAllowedSources();
        if (Array.isArray(sources)) {
          options.leadSources = sources.map((s) => ({ id: s, name: s }));
          const mapped = sources
            .map((s) => ({ value: s, name: s }))
            .filter((s) => s.value !== "None");

          dropdownEnums.enum_BasicLeadSource = mapped;
          dropdownEnums.BasicLeadSource = dropdownEnums.createEnum(mapped);
        }
      } catch (err) {
        console.error("Error loading allowed sources dynamically:", err);
      }
    }

    const skipUsersFetch =
      user?.RoleType === Enums.Roles.SchedulingUser ||
      user?.RoleType === Enums.Roles.SchedulingTeamManager;

    if (resolvedDataClient?.lookups) {
      try {
        const promises = [
          resolvedDataClient.lookups.getCountries().then((res) => {
            options.countries = res.countries;
          }),
          resolvedDataClient.lookups.getStatesProvinces().then((res) => {
            options.statesProvinces = res.statesProvinces;
          }),
          resolvedDataClient.lookups.getLanguages
            ? resolvedDataClient.lookups
                .getLanguages()
                .then((res) => {
                  options.languages = res.languages;
                })
                .catch((err) => {
                  console.error(
                    "Error fetching languages from lookups REST API:",
                    err
                  );
                })
            : Promise.resolve(),
        ];

        if (!isPublic) {
          promises.push(
            resolvedDataClient.lookups.getIndustries().then((res) => {
              options.industries = res.industries;
            }),
            resolvedDataClient.lookups.getBusinessCategories().then((res) => {
              const bc = res.businessCategory;
              options.businessCategory = Array.isArray(bc)
                ? bc
                : (bc?.items ?? []);
            })
          );
          if (!skipUsersFetch) {
            promises.push(
              resolvedDataClient.lookups.getSystemUsers().then((res) => {
                options.systemUsers = res.systemUsers;
              })
            );
          }
        }

        await Promise.all(promises);
      } catch (err) {
        console.error(
          "Error fetching lookups in getOptionsForCreateLead:",
          err
        );
      }
    }

    if (!isPublic && !skipUsersFetch && resolvedDataClient?.users?.getAll) {
      try {
        const users = await resolvedDataClient.users.getAll();
        if (users?.items && users?.items?.length) {
          options.users = users.items.map((user) => ({
            ...user,
            id: user?.sourceId || user.userId || user.id,
            role:
              user.role ||
              (Array.isArray(user.roles) ? user.roles[0] : user.roles),
          }));
        }
      } catch (err) {
        console.error(
          "Error fetching users from frontdoor in getOptionsForCreateLead:",
          err
        );
      }
    }
    if (options?.users?.length) {
      options.users = sortByDisplayName(
        options.users.filter((x) => crmRoles.includes(x.role))
      );
    }
    if (options?.reportTemplateCategory?.items?.length) {
      options.reportTemplateCategory = options.reportTemplateCategory.filter(
        (r) => r.id
      );
    }
    dispatch(setLeadOptionsToState(options));
    return options; // Return the data fetched from the server
  };

export const getLeadStatuses = (state) => {
  return state.dropdownOptions?.dropdownOptions?.leadStatuses;
};

export const getCrmMembers = (state) => {
  return state.dropdownOptions?.dropdownOptions?.crmMembers;
};

export const getCrmCalendars = (state) => {
  return state.dropdownOptions?.dropdownOptions?.crmCalendars;
};

export const getLanguages = (state) => {
  return state.dropdownOptions?.dropdownOptions?.languages;
};

export const getLeadSources = (state) => {
  return state.dropdownOptions?.dropdownOptions?.leadSources;
};

export const getRequestTypes = (state) => {
  return state.dropdownOptions.dropdownOptions?.requestTypes;
};

export const getUsers = (state) => {
  return state.dropdownOptions?.dropdownOptions?.users;
};
export const getSystemUsers = (state) => {
  return state.dropdownOptions?.dropdownOptions?.systemUsers;
};

export const getPreferredContact = (state) => {
  return state.dropdownOptions?.dropdownOptions?.preferredContact;
};
export const getTagColors = (state) => {
  return state.dropdownOptions?.dropdownOptions?.tagColors;
};
export const getBusinessCategories = (state) => {
  const data = state.dropdownOptions?.dropdownOptions?.businessCategory;
  return Array.isArray(data) ? data : (data?.items ?? []);
};

export const getDomainEmailState = (state) => {
  return state.dropdownOptions?.dropdownOptions?.domainEmails;
};
export const getWFTemplateCategories = (state) => {
  return state.dropdownOptions?.dropdownOptions?.wfTemplateCategories;
};
export const GetTaskStatuses = (state) => {
  return state.dropdownOptions?.dropdownOptions?.taskStatuses;
};
export const GetTaskTypes = (state) => {
  return state.dropdownOptions?.dropdownOptions?.taskTypes;
};
export const GetTaskPriorities = (state) => {
  return state.dropdownOptions?.dropdownOptions?.taskPriorities;
};

export const getDropdownOptions =
  (excludedRoles = []) =>
  (state) => {
    const dropdownOptions = state.dropdownOptions?.dropdownOptions ?? {};
    const options = JSON.parse(JSON.stringify(dropdownOptions));
    if (options?.users?.length && excludedRoles?.length) {
      options.users = options?.users?.filter(
        (user) => !excludedRoles.includes(user.role)
      );
    }

    return options;
  };
export const getDropdownOptionsWithoutRole = (state) => {
  const dropdownOptions = state.dropdownOptions?.dropdownOptions ?? {};
  return JSON.parse(JSON.stringify(dropdownOptions));
};

export default dropdownOptions.reducer;
